import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarDays, Activity, Images, CheckSquare, HardDrive, Camera, PlusCircle, ArrowUpRight,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip, BarChart, Bar } from "recharts";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common/Loader";
import { PageHeader, StatCard, StatusBadge } from "@/components/dashboard/ui";

const spark = [
  { d: "Mon", v: 12 }, { d: "Tue", v: 34 }, { d: "Wed", v: 28 },
  { d: "Thu", v: 52 }, { d: "Fri", v: 41 }, { d: "Sat", v: 78 }, { d: "Sun", v: 64 },
];

export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader label="Loading your studio" />;

  const s = data.stats;
  const storagePct = Math.min(100, Math.round((s.storage_used_gb / s.storage_limit_gb) * 100));

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${(user?.name || "").split(" ")[0]}`}
        subtitle="Here's what's happening across your events."
        action={<button data-testid="create-event-btn" onClick={() => navigate("/app/events/new")} className="inline-flex items-center gap-2 bg-gold text-black font-medium rounded-full px-5 py-2.5 hover:bg-gold-light transition-colors"><PlusCircle className="w-4 h-4" /> Create Event</button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarDays} label="Total Events" value={s.total_events} accent />
        <StatCard icon={Activity} label="Active Events" value={s.active_events} />
        <StatCard icon={Images} label="Total Photos" value={s.total_photos.toLocaleString()} />
        <StatCard icon={CheckSquare} label="Photos Selected" value={s.photos_selected.toLocaleString()} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-black/40 p-6">
          <div className="flex items-center justify-between mb-4">
            <div><div className="text-white font-medium">Gallery activity</div><div className="text-xs text-muted-foreground">Views & searches this week</div></div>
            <span className="text-xs text-gold">+18% vs last week</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={spark}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#d4af37" stopOpacity={0.5} /><stop offset="100%" stopColor="#d4af37" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="d" stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #262626", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="v" stroke="#d4af37" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-black/40 p-6">
          <div className="flex items-center gap-2 mb-4"><HardDrive className="w-4 h-4 text-gold" /><span className="text-white font-medium">Storage</span></div>
          <div className="font-serif text-4xl text-white">{s.storage_used_gb}<span className="text-lg text-muted-foreground"> / {s.storage_limit_gb} GB</span></div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-4"><div className="h-full bg-gold" style={{ width: `${storagePct}%` }} /></div>
          <div className="text-xs text-muted-foreground mt-2">{storagePct}% used on {data.plan?.name} plan</div>
          <Link to="/app/subscription" className="mt-4 inline-flex items-center gap-1 text-xs text-gold hover:underline">Upgrade plan <ArrowUpRight className="w-3 h-3" /></Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3"><h2 className="text-white font-medium">Recent events</h2><Link to="/app/events" className="text-xs text-gold hover:underline">View all</Link></div>
          <div className="space-y-3">
            {data.events.map((e) => {
              const pct = e.photo_count ? Math.round((e.selected_count / e.photo_count) * 100) : 0;
              return (
                <Link key={e.id} to={`/app/events/${e.id}`} data-testid={`event-row-${e.id}`}
                  className="flex items-center gap-4 rounded-xl border border-border bg-black/40 p-3 hover:border-gold/30 transition-colors group">
                  <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-secondary">
                    {e.cover_image && <img src={mediaUrl(e.cover_image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="text-white font-medium truncate">{e.name}</span><StatusBadge status={e.selection_status} /></div>
                    <div className="text-xs text-muted-foreground mt-0.5">{e.event_type} · {e.photo_count} photos · {e.selected_count} selected</div>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-2 max-w-xs"><div className="h-full bg-gold" style={{ width: `${pct}%` }} /></div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                </Link>
              );
            })}
          </div>
        </div>

        <div>
          <h2 className="text-white font-medium mb-3">Activity timeline</h2>
          <div className="rounded-2xl border border-border bg-black/40 p-5 space-y-4">
            {data.activity.length === 0 && <p className="text-sm text-muted-foreground">No recent activity.</p>}
            {data.activity.map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="mt-1 w-2 h-2 rounded-full bg-gold shrink-0" />
                <p className="text-sm text-secondary-foreground leading-snug">{a.message}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
